'use client'

import { useMutation } from '@tanstack/react-query'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { schoolGradeLabel } from '@helper/utils/domain'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { Student, StudentAction } from '../types'
import { useSharedTRPC } from '../../../context'
import { useStudentMutations } from '../use-student-mutations'

interface RemoveStudentDialogProps {
  student: Student
  /** Lets the dialog hand the flow over to `RequestHomeConnectDialog` without nesting a dialog. */
  onAction: (action: StudentAction) => void
  onClose: () => void
}

/**
 * Removes one student from the school — and says which of the two possible outcomes that is.
 *
 * The server decides by membership count, not by anything sent from here: a child who also belongs
 * to a family loses only the school membership, a child whose sole home is this school loses the
 * account. That is exactly what `parentConnection` predicts, so the copy branches on it. Getting
 * this wrong in either direction is expensive — promising permanent deletion for a child who
 * survives makes the teacher hesitate, and promising survival for one who does not loses a family's
 * reading history without warning.
 *
 * `pending` counts as not-connected: a request nobody has accepted holds no child, so the deletion
 * really is permanent. The slot that would offer "Sambungkan dulu" reports the outstanding request
 * instead, since asking again would only be refused.
 */
export function RemoveStudentDialog({ student, onAction, onClose }: RemoveStudentDialogProps) {
  const trpc = useSharedTRPC()
  const { invalidate } = useStudentMutations()

  const deleteChildren = useMutation(trpc.myOrganization.deleteChildren.mutationOptions())

  const connection = student.parentConnection
  const isConnected = connection.status === 'connected'
  const gradeLabel = schoolGradeLabel(student.schoolGrade)

  const handleConfirm = async () => {
    try {
      await deleteChildren.mutateAsync({ userIds: [student.id] })
      await invalidate()
      toast.success(`${student.name} berhasil dikeluarkan dari sekolah.`)
      onClose()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal mengeluarkan murid')
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className='srd:w-full srd:max-w-md'>
        <DialogHeader>
          <DialogTitle>Yakin keluarkan murid dari sekolah ini?</DialogTitle>
          <DialogDescription>
            {isConnected ? (
              <>
                Murid akan dikeluarkan dari sekolah ini. Semua data riwayat aktivitasnya selama berada di sekolah ini
                tetap tersimpan. Murid masih dapat mengakses akun mereka melalui akun orang tua.
              </>
            ) : (
              <>
                Akun ini akan <strong>dihapus permanen.</strong> Sambungkan dengan orang tuanya dulu agar riwayat
                belajarnya tersimpan.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className='srd:flex srd:flex-nowrap srd:items-center srd:gap-x-2'>
          <Avatar profile='user' size='lg'>
            {student.image && <AvatarImage src={student.image} alt={student.name} />}
            <AvatarFallback>{getInitials(student.name)}</AvatarFallback>
          </Avatar>
          <span className='srd:flex srd:flex-col'>
            <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
              {student.name}
            </span>
            {gradeLabel && (
              <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>
                {gradeLabel}
              </span>
            )}
          </span>
        </div>

        <div className='srd:flex srd:flex-col srd:gap-y-2'>
          <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>Orang tua</span>

          {connection.status === 'connected' ? (
            <div className='srd:flex srd:flex-nowrap srd:items-center srd:gap-x-2'>
              <Avatar profile='user' size='lg'>
                <AvatarFallback>
                  {getInitials(connection.parentName ?? connection.parentEmail ?? 'Orang tua')}
                </AvatarFallback>
              </Avatar>
              <span className='srd:flex srd:flex-col'>
                <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
                  {connection.parentName ?? 'Orang tua'}
                </span>
                {connection.parentEmail && (
                  <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>
                    {connection.parentEmail}
                  </span>
                )}
              </span>
            </div>
          ) : connection.status === 'pending' ? (
            <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>
              Menunggu jawaban {connection.parentEmail}
            </span>
          ) : (
            <Button
              variant='outline'
              className='srd:w-fit'
              disabled={deleteChildren.isPending}
              onClick={() => onAction({ kind: 'request-home-connect', student })}
            >
              Sambungkan dulu
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={deleteChildren.isPending}>
            Batal
          </Button>
          <Button
            variant='destructive'
            onClick={() => void handleConfirm()}
            loading={deleteChildren.isPending}
            disabled={deleteChildren.isPending}
          >
            {isConnected ? 'Ya, keluarkan' : 'Ya, hapus saja'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
